import { useMemo } from "react";
import ProductCard from "@/pages/home/components/ProductCard";
import { useProductsByIds, useVariantsByProductIds } from "@/hooks/useProducts";

interface RecentlyViewedProps {
  ids: number[];
  onClear: () => void;
}

export default function RecentlyViewed({ ids, onClear }: RecentlyViewedProps) {
  const { products, loading } = useProductsByIds(ids);

  // Preserve order from recent list (most recent first)
  const ordered = useMemo(() => {
    const map = new Map(products.map((p) => [p.id, p]));
    return ids.map((id) => map.get(id)).filter((p): p is NonNullable<typeof p> => !!p);
  }, [products, ids]);

  const productIds = useMemo(() => ordered.map((p) => p.id), [ordered]);
  const { variantsByProduct } = useVariantsByProductIds(productIds);

  if (!loading && ordered.length === 0) return null;

  return (
    <section className="w-full bg-background-100/40 border-t border-background-200/70">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-12 md:py-14">
        <div className="flex items-end justify-between gap-4 mb-6 md:mb-8">
          <div>
            <p className="text-[12px] uppercase tracking-[0.18em] text-foreground-600 mb-2 inline-flex items-center gap-1.5">
              <i className="ri-time-line text-[13px]"></i>
              Vistos recentemente
            </p>
            <h2 className="font-heading text-foreground-950 text-2xl md:text-3xl font-medium">
              Continue de onde parou
            </h2>
          </div>
          {ordered.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="hidden sm:inline-flex items-center gap-1.5 text-[13px] text-foreground-700 hover:text-foreground-950 underline underline-offset-4 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-eraser-line text-[14px]"></i>
              Limpar histórico
            </button>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 animate-pulse">
            {Array.from({ length: Math.min(4, ids.length || 4) }).map((_, i) => (
              <div key={i}>
                <div className="aspect-square rounded-md bg-background-200/60"></div>
                <div className="h-3 bg-background-200/60 rounded mt-3 w-3/4"></div>
                <div className="h-3 bg-background-200/60 rounded mt-2 w-1/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {ordered.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                variants={variantsByProduct[p.id] ?? []}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}